// Kizuna Link main page: pick a game, connect the GB-Link, make/join a relay room, then bridge
// device <-> relay (docs/81). Device statuses go up, relay link commands come down, 64-byte
// link frames flow both ways.

import "./routes";
import { FirmwareInfo, Status, formatVersion, statusName, versionAtLeast } from "./gblink";
import { FALLBACK_PROFILES, Profile, loadProfiles } from "./profiles";
import { trProfile } from "./profile_i18n";
import {
  RelayHttpError,
  RelaySocket,
  ROOM_CODE_RE,
  Seat,
  createRoom,
  joinRoom,
  normaliseRoomCode,
} from "./relay";
import { BootModeError, ClaimError, GbLinkDevice, GbLinkEvents } from "./usb";

type StepId = "game" | "device" | "room" | "wait" | "play" | "linked";
const STEP_ORDER: StepId[] = ["game", "device", "room", "wait", "play", "linked"];

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

const ui = {
  games: $("games"),
  connect: $<HTMLButtonElement>("connect"),
  deviceMsg: $("device-msg"),
  create: $<HTMLButtonElement>("create"),
  joinForm: $<HTMLFormElement>("join-form"),
  joinCode: $<HTMLInputElement>("join-code"),
  join: $<HTMLButtonElement>("join"),
  roomMsg: $("room-msg"),
  roomCode: $("room-code"),
  copyCode: $<HTMLButtonElement>("copy-code"),
  copyInvite: $<HTMLButtonElement>("copy-invite"),
  waitMsg: $("wait-msg"),
  gameHint: $("game-hint"),
  playMsg: $("play-msg"),
  linkedMsg: $("linked-msg"),
  sessionActions: $("session-actions"),
  leave: $<HTMLButtonElement>("leave"),
  diagSum: $("diag-sum"),
  copyLog: $<HTMLButtonElement>("copy-log"),
  log: $("log"),
};

const state = {
  profiles: [] as Profile[],
  profile: null as Profile | null,
  device: null as GbLinkDevice | null,
  firmware: null as FirmwareInfo | null,
  seat: null as Seat | null,
  socket: null as RelaySocket | null,
  peerPresent: false,
  modeStarted: false,
  linked: false,
  framesUp: 0,
  framesDown: 0,
};

// ---------- helpers ----------

function log(text: string): void {
  const t = new Date();
  const ts = `${t.toLocaleTimeString([], { hour12: false })}.${String(t.getMilliseconds()).padStart(3, "0")}`;
  const line = document.createElement("div");
  line.textContent = `${ts}  ${text}`;
  ui.log.append(line);
  while (ui.log.childElementCount > 1500) ui.log.firstElementChild?.remove();
  ui.log.scrollTop = ui.log.scrollHeight;
}

function setStep(active: StepId): void {
  const idx = STEP_ORDER.indexOf(active);
  document.querySelectorAll<HTMLElement>(".step").forEach((el) => {
    const i = STEP_ORDER.indexOf(el.dataset.step as StepId);
    el.classList.toggle("done", i < idx || (active === "linked" && state.linked && i === idx));
    el.classList.toggle("active", i === idx);
  });
}

function sum(id: StepId, text: string): void {
  $(`sum-${id}`).textContent = text;
}

function msg(el: HTMLElement, text: string, error = false): void {
  el.textContent = text;
  el.classList.toggle("error", error);
}

function updateDiag(): void {
  const parts = [
    state.device ? `device ${state.device.productName}` : "no device",
    state.seat ? `room ${state.seat.code} seat ${state.seat.seat}` : "no room",
    `frames up ${state.framesUp} / down ${state.framesDown}`,
  ];
  ui.diagSum.textContent = parts.join(" · ");
}

async function copy(text: string, btn: HTMLButtonElement): Promise<void> {
  const old = btn.textContent;
  try {
    await navigator.clipboard.writeText(text);
    btn.textContent = "Copied!";
  } catch {
    btn.textContent = "Copy failed";
  }
  setTimeout(() => (btn.textContent = old), 1400);
}

// ---------- step 1: games ----------

function renderGames(): void {
  ui.games.textContent = "";
  for (const p of state.profiles) {
    const b = document.createElement("button");
    b.className = "game";
    b.type = "button";
    b.disabled = !p.enabled;
    b.setAttribute("aria-pressed", String(state.profile?.id === p.id));
    b.innerHTML = `<span class="g-label"></span><span class="g-detail"></span><span class="g-games"></span>`;
    (b.children[0] as HTMLElement).textContent = trProfile(p.label) ?? p.label;
    (b.children[1] as HTMLElement).textContent = trProfile(p.detail) ?? p.detail;
    (b.children[2] as HTMLElement).textContent = trProfile(p.games) ?? p.games;
    if (!p.enabled) {
      const s = document.createElement("span");
      s.className = "soon";
      s.textContent = trProfile(p.note) ?? trProfile("Coming soon") ?? "Coming soon";
      b.append(s);
    }
    b.addEventListener("click", () => chooseGame(p));
    ui.games.append(b);
  }
}

function chooseGame(p: Profile): void {
  if (state.seat) return; // locked once in a room
  state.profile = p;
  renderGames();
  sum("game", `${trProfile(p.label) ?? p.label} · ${trProfile(p.detail) ?? p.detail}`);
  ui.gameHint.textContent = trProfile(p.inGameHint) ?? p.inGameHint ?? "";
  log(`game: ${p.id}`);
  checkFirmwareFits();
  setStep(state.device ? "room" : "device");
}

// ---------- step 2: device ----------

const deviceEvents: GbLinkEvents = {
  status(code) {
    log(`device ${statusName(code)}`);
    if (code >= Status.GameboyDisconnected && code <= Status.LinkClosed) state.socket?.sendStatus(code);
    onDeviceStatus(code);
  },
  linkData(frame) {
    state.framesUp++;
    state.socket?.sendLinkData(frame);
    if (state.framesUp % 50 === 0) updateDiag();
  },
  shortData() {
    // Command replies and wireless-mode telemetry; never relayed.
  },
  disconnected(reason) {
    log(`device disconnected: ${reason}`);
    state.device = null;
    state.modeStarted = false;
    sum("device", "");
    msg(ui.deviceMsg, "Your GB-Link was disconnected. Plug it back in and connect again.", true);
    if (state.seat) msg(ui.playMsg, "Your GB-Link was disconnected. Reconnect it in step 2.");
    setStep("device");
    updateDiag();
  },
};

let firmwareText = "";

async function attachDevice(d: GbLinkDevice): Promise<void> {
  state.device = d;
  log(`opened ${d.productName}`);
  try {
    const fw = await d.firmwareInfo();
    state.firmware = fw;
    firmwareText = formatVersion(fw);
    log(`firmware ${firmwareText}`);
  } catch (e) {
    state.firmware = null;
    firmwareText = "unknown";
    log(`firmware info failed: ${(e as Error).message}`);
  }
  sum("device", `Connected · firmware ${firmwareText}`);
  msg(ui.deviceMsg, "");
  updateDiag();
  if (!checkFirmwareFits()) return;
  if (state.seat) {
    setStep(state.peerPresent ? "play" : "wait");
    maybeStartMode();
  } else {
    setStep(state.profile ? "room" : "game");
  }
}

function openSetup(): void {
  const setup = $<HTMLDetailsElement>("setup");
  setup.open = true;
  setup.scrollIntoView({ behavior: "smooth", block: "nearest" });
}

$("not-listed").addEventListener("click", (e) => {
  e.preventDefault();
  openSetup();
});

function checkFirmwareFits(): boolean {
  const fw = state.firmware;
  const min = state.profile?.minFirmware;
  if (fw && min && !versionAtLeast(fw, ...min)) {
    msg(ui.deviceMsg, `This needs GB-Link firmware ${min.join(".")} or newer (yours is ${formatVersion(fw)}). Update it with the GB-Link Launcher (launcher.gblink.io → Device Health), then connect again.`, true);
    openSetup();
    setStep("device");
    return false;
  }
  return true;
}

ui.connect.addEventListener("click", async () => {
  if (state.device) await state.device.close();
  try {
    await attachDevice(await GbLinkDevice.request(deviceEvents));
  } catch (e) {
    if (e instanceof DOMException && e.name === "NotFoundError") return; // picker closed
    if (e instanceof BootModeError) {
      msg(ui.deviceMsg, "This Pico is in boot mode and doesn't have GB-Link firmware yet. Install it using the steps above, then connect again.", true);
      openSetup();
      log("picked an RP2 boot-mode device");
      return;
    }
    msg(ui.deviceMsg, e instanceof ClaimError ? e.message : `Couldn't connect: ${(e as Error).message}`, true);
    log(`connect failed: ${(e as Error).message}`);
  }
});

// ---------- step 3: room ----------

async function enterRoom(make: () => Promise<Seat>, busy: HTMLButtonElement): Promise<void> {
  if (!state.profile?.family) return;
  busy.disabled = true;
  msg(ui.roomMsg, "");
  try {
    const seat = await make();
    state.seat = seat;
    log(`room ${seat.code}, seat ${seat.seat}`);
    ui.roomCode.textContent = seat.code;
    sum("room", seat.code);
    history.replaceState(null, "", `?game=${encodeURIComponent(state.profile.id)}&room=${seat.code}`);
    ui.sessionActions.hidden = false;
    openSocket(seat);
    setStep("wait");
  } catch (e) {
    const err = e as Error;
    let text = `Couldn't reach Kizuna's servers: ${err.message}`;
    if (e instanceof RelayHttpError) {
      if (e.status === 404) text = "No room with that code. Check it with your friend - rooms close after 30 minutes without anyone in them.";
      else if (e.status === 409) text = "That room is for a different game. Make sure you both picked the same option in step 1.";
      else if (e.status === 400) text = "That doesn't look like a room code. It should look like KZN-ABCD.";
    }
    msg(ui.roomMsg, text, true);
    log(`room failed: ${err.message}`);
  } finally {
    busy.disabled = false;
    updateDiag();
  }
}

ui.create.addEventListener("click", () => enterRoom(() => createRoom(state.profile!.family!), ui.create));

ui.joinForm.addEventListener("submit", (e) => {
  e.preventDefault();
  const code = normaliseRoomCode(ui.joinCode.value);
  if (!ROOM_CODE_RE.test(code)) {
    msg(ui.roomMsg, "That doesn't look like a room code. It should look like KZN-ABCD.", true);
    return;
  }
  ui.joinCode.value = code;
  void enterRoom(() => joinRoom(code, state.profile!.family!), ui.join);
});

ui.copyCode.addEventListener("click", () => state.seat && copy(state.seat.code, ui.copyCode));
ui.copyInvite.addEventListener("click", () => {
  if (!state.seat || !state.profile) return;
  const url = `${location.origin}/?game=${encodeURIComponent(state.profile.id)}&room=${state.seat.code}`;
  void copy(url, ui.copyInvite);
});

// ---------- steps 4-6: session ----------

let deviceWrites: Promise<unknown> = Promise.resolve();

function openSocket(seat: Seat): void {
  const socket = new RelaySocket(seat.token, {
    welcome(s, peerPresent) {
      log(`relay welcome: seat ${s}, friend ${peerPresent ? "here" : "not here yet"}`);
      if (peerPresent) onPeerPresent();
    },
    peerUp() {
      log("friend joined");
      onPeerPresent();
    },
    peerDown(dropped) {
      log(`friend left (${dropped ? "connection dropped" : "closed"})`);
      ui.waitMsg.textContent = "Your friend lost connection. Waiting for them to come back…";
      if (!state.linked) msg(ui.playMsg, "Your friend lost connection. Waiting for them to come back…");
      else ui.linkedMsg.textContent = "Your friend lost connection. If they come back within a few seconds the link carries on.";
    },
    sessionClose() {
      log("session closed by relay");
      endSession("Session finished. Thanks for trading! Make a new room to trade again.");
    },
    linkCmd(cmd) {
      log(`relay command 0x${cmd.toString(16)}`);
      void state.device?.command(cmd).catch((e) => log(`device command failed: ${e.message}`));
    },
    linkData(frame) {
      state.framesDown++;
      const d = state.device;
      if (!d) return;
      deviceWrites = deviceWrites.then(() => d.sendLinkData(frame)).catch((e) => log(`device write failed: ${e.message}`));
    },
    socketState(s) {
      log(`relay socket ${s}`);
      if (s === "reconnecting") msg(ui.playMsg, "Reconnecting to Kizuna's servers…");
    },
    log,
  });
  state.socket = socket;
  socket.connect();
}

function onPeerPresent(): void {
  state.peerPresent = true;
  ui.waitMsg.textContent = "Your friend is here!";
  sum("wait", "Friend joined");
  if (!state.device) {
    setStep("device");
    msg(ui.deviceMsg, "Your friend is in the room. Connect your GB-Link to continue.");
    return;
  }
  setStep(state.linked ? "linked" : "play");
  maybeStartMode();
}

async function maybeStartMode(): Promise<void> {
  const d = state.device;
  const p = state.profile;
  if (!d || !p?.mode || !state.peerPresent || state.modeStarted) return;
  state.modeStarted = true;
  msg(ui.playMsg, "Getting your GB-Link ready…");
  try {
    await d.startMode(p.mode, p.variant);
    log("device ready");
  } catch (e) {
    state.modeStarted = false;
    msg(ui.playMsg, `Your GB-Link didn't respond (${(e as Error).message}). Unplug it, plug it back in and connect again.`, true);
    log(`start failed: ${(e as Error).message}`);
  }
}

function onDeviceStatus(code: number): void {
  const live = ui.playMsg;
  live.classList.remove("ok");
  switch (code) {
    case Status.AwaitMode:
      msg(live, "Ready. Now go to the Pokémon Center in your game.");
      break;
    case Status.HandshakeReceived:
      msg(live, "Found your Game Boy! Waiting for your friend's game…");
      break;
    case Status.LinkConnected:
      state.linked = true;
      live.classList.add("ok");
      msg(live, "Linked!");
      sum("play", "Done");
      sum("linked", "Connected");
      setStep("linked");
      break;
    case Status.LinkReconnecting:
      ui.linkedMsg.textContent = "The link is catching up… hold on a moment.";
      break;
    case Status.LinkClosed:
      ui.linkedMsg.textContent = "You left the room in the game. Waiting for your friend to leave too…";
      break;
  }
}

function endSession(text: string): void {
  void state.device?.cancel().catch(() => {});
  state.socket?.close();
  state.socket = null;
  state.seat = null;
  state.peerPresent = false;
  state.modeStarted = false;
  state.linked = false;
  ui.sessionActions.hidden = true;
  history.replaceState(null, "", state.profile ? `?game=${encodeURIComponent(state.profile.id)}` : location.pathname);
  ["room", "wait", "play", "linked"].forEach((s) => sum(s as StepId, ""));
  ui.waitMsg.textContent = "Waiting for your friend to join…";
  ui.linkedMsg.textContent = "You're connected. Trade as if your friend were sitting next to you. When you're done, leave the room in the game.";
  msg(ui.roomMsg, text);
  setStep(state.device ? "room" : "device");
  updateDiag();
}

ui.leave.addEventListener("click", () => {
  log("left the room");
  endSession("You left the room.");
});

// Let people go back and change their game until they're in a room.
document.querySelector<HTMLElement>('[data-step="game"] .step-head')!.addEventListener("click", () => {
  if (!state.seat) setStep("game");
});

ui.copyLog.addEventListener("click", () => copy(ui.log.innerText, ui.copyLog));

addEventListener("pagehide", () => void state.device?.cancel().catch(() => {}));

// ---------- boot ----------

async function boot(): Promise<void> {
  state.profiles = FALLBACK_PROFILES;
  renderGames();
  setStep("game");
  state.profiles = await loadProfiles();
  renderGames();

  if (!GbLinkDevice.isSupported()) {
    $("unsupported").hidden = false;
    ui.connect.disabled = true;
    return;
  }

  const params = new URLSearchParams(location.search);
  const gameId = params.get("game");
  const room = params.get("room");
  const p = state.profiles.find((x) => x.id === gameId && x.enabled);
  if (p) chooseGame(p);
  if (room) ui.joinCode.value = normaliseRoomCode(room);

  navigator.usb.addEventListener("disconnect", (e) => {
    if (state.device && e.device === state.device.usb) deviceEvents.disconnected("unplugged");
  });
  try {
    const d = await GbLinkDevice.reopen(deviceEvents);
    if (d) await attachDevice(d);
  } catch (e) {
    log(`auto-connect: ${(e as Error).message}`);
  }
  updateDiag();
}

void boot();
